import { useAnimations } from "@react-three/drei";
import { useEffect, useMemo, useRef } from "react";
import { findObjectStartsWithKey, useDracoGLTF } from "@utils";
import WeldacProcess from "./WeldacProcess";
import { Ebbne, MountWithEbbne, useEbbne, useEbbnenContext } from "@solidwhite/ebbnen";
import SetCameraTarget from "../../SetCameraTarget";
import { SideBarHider } from "@ui/SideBar/SideBar";
import { WeldacFamily } from "./WeldacFamily.tsx";
import { WeldacInterior } from "./WeldacInterior.tsx";
import { Labels } from "@ui/Labels.tsx";
import { Vector3 } from "three";
import VisibleWithEbbne from "../../VisibleWithEbbne.tsx";
import { config, useSpring, animated } from "@react-spring/three";
import { useSpringValue } from "@react-spring/web";
import WeldacSystem from "./WeldacSystem.tsx";
import EnvironmentRotationSetter from "../../EnvironmentRotationSetter.tsx";

import useAuth from "../../../../hooks/useAuth";

const Weldac = () => {
  const { nodes, scene, animations } = useDracoGLTF("./models/heat/Weldac_Main.glb");
  const { currentId } = useEbbnenContext();
  const cover = useRef(null);

  const { currentUser } = useAuth();

  useEffect(() => {
    const find = findObjectStartsWithKey("Weldac_Animation_Cover", scene);
    if (find) cover.current = find;
  });

  const hotSpots = useMemo(() => {
    const labelRoot = findObjectStartsWithKey("Labels_Root", scene);
    return labelRoot?.children || [];
  }, []);

  const cameraTargets = useMemo(() => {
    const obj = findObjectStartsWithKey("CameraTargets_All", scene);
    const camTargets = {};
    obj.children.forEach((c) => {
      if (c.userData.route) camTargets[c.userData.route] = c;
    });

    return camTargets;
  }, []);

  const { position } = useSpring({
    from: { position: [0, 0, 0] },
    to: { position: currentId === "interior" ? [-7, -2, 0] : [0, 0, 0] },
    config: config.molasses,
    onChange: ({ value }) => {
      if (cover.current) {
        cover.current.position.set(...value.position);
      }
    },
  });

  return (
    <>
      <MountWithEbbne mountedWhen={"visible"}>
        {!["family", "weldac-system-overview"].includes(currentId) && <primitive object={scene} />}
      </MountWithEbbne>

      <MountWithEbbne>
        <group position={nodes.Weldac_1.position} rotation={nodes.Weldac_1.rotation} scale={nodes.Weldac_1.scale}>
          <Labels data={hotSpots} />
        </group>

        <EnvironmentRotationSetter />
      </MountWithEbbne>

      <Ebbne id="family">
        <WeldacFamily />
      </Ebbne>

      <Ebbne id="interior" visibleWithAbove={1}>
        {currentUser && <WeldacInterior />}
      </Ebbne>

      <Ebbne id="process-weldac" visibleWithAbove={2}>
        <WeldacProcess />
      </Ebbne>

      <Ebbne id="weldac-system-overview" visibleWithAbove={0} visibleWithBelow={1}>
        <WeldacSystem />
      </Ebbne>
    </>
  );
};

export default Weldac;

useDracoGLTF.preload("./models/heat/Weldac_Main.glb");
